import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { getServerErrors } from '../../common/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import ConnectActiveCampaignMutation from './ConnectActiveCampaignMutation';
import UI from '../../common/UI';
import useMutation from '../../api/useMutation';
import useProject from '../useProject';
import useValidation from '../../common/useValidation';

const schema = yup.object().shape({
  api_url: yup.string().ensure().required(),
  api_key: yup.string().ensure().required(),
});

export interface AddActiveCampaignIntegrationFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const AddActiveCampaignIntegrationForm = ({
  onSuccess, onCancel,
}: AddActiveCampaignIntegrationFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      api_url: '',
      api_key: '',
    },
  });

  const [connectActiveCampaign, { loading, error }] = useMutation(
    ConnectActiveCampaignMutation,
    {
      variables: {
        input: {
          project_id: project.id,
          ...form.values,
        },
      },
      refetchQueries: ['GetIntegrationsForProject'],
      onCompleted: () => {
        onSuccess?.();
        notifier.success(t('notifications.active_campaign_connected'));
        form.reset();
      },
      onError: () => {
        //
      },
    },
  );

  const { valid, errors } = useValidation({
    schema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => connectActiveCampaign()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>{t('connect_active_campaign_title')}</UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('connect_active_campaign_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup sc={{ valid: !errors.api_url, touched: form.touched.api_url }}>
          <UI.InputLabel htmlFor="ActiveCampaign_ApiUrl">
            {t('api_url')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.Input
            {...form.text('api_url')}
            id="ActiveCampaign_ApiUrl"
            placeholder="https://youraccount.api-us1.com"
            autoComplete="off"
          />
          <UI.ErrorMessages attribute={t('api_url')} errors={errors.api_url} />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.api_key, touched: form.touched.api_key }}>
          <UI.InputLabel htmlFor="ActiveCampaign_ApiKey">
            {t('api_key')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.Input
            {...form.text('api_key')}
            id="ActiveCampaign_ApiKey"
            autoComplete="off"
          />
          <UI.ErrorMessages attribute={t('api_key')} errors={errors.api_key} />
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save')}
          onClose={onCancel}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default AddActiveCampaignIntegrationForm;
